import React from 'react';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import LessIcon from '@material-ui/icons/ExpandLess';
import MoreIcon from '@material-ui/icons/ExpandMore';
import GoogleMapReact, { Coords } from 'google-map-react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { match, Link as RouterLink, RouteComponentProps } from 'react-router-dom';

//Components
import { ActionLogs } from '../../../components/action-logs';
import { Anchor } from '../../../components/anchor';
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedListItem from '../../../components/listItem';
import MyLink from '../../../components/mylink';
import { TooltipButton } from '../../../components/tooltip-button';
import { AnchorBlock } from '../../../components/anchorBlock';
import { Dialog } from '../../../components/dialog';
// Modules
import {
    Reservation,
    ReservationById,
    BookingCancelPayload,
    ReservationStatus,
    TParams,
} from '../../../redux/reducers/api-types';
import { actions as errorActions, ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import {
    actions as reservationActions,
    selectors as reservationSelectors,
    types as reservationTypes,
} from '../../../redux/reducers/reservations';
// Ressources
import AddressUtils from '../../../utils/addressUtils';
import FormattingUtils from '../../../utils/formattingUtils';
import MapUtils from '../../../utils/mapUtils';
import withGlobalStyles from '../../../utils/global-styles';
import DateUtils from '../../../utils/dateUtils';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { createPath, paths } from '../../paths';
import { DialogTextColor } from '../../../components/dialog/dialog';
import { getCostCenterDetails } from '../../../utils/businessUtils';
import { clsx } from '../../../utils/htmlUtils';
import { FormatType, ListCell } from '../../../local-types';
import envConfig from '../../../config';

interface ReservationViewProps extends WithTranslation, RouteComponentProps {
    match: match<TParams>;
    globalClasses: any;
    center: Coords;
    zoom: number;
    reservations: ReservationById;
    getReservationError: ErrorPayload;
    clearError: (errorType: string) => void;
    splitReservationError: ErrorPayload;
    cancelReservationError: ErrorPayload;
    cancelReservationCallCenterError: ErrorPayload;
    getReservation: (id: string) => void;
    splitReservation: (id: string, callback: Function) => void;
    cancelReservation: (id: string, payload: BookingCancelPayload, callback: Function) => void;
    cancelReservationCallCenter: (payload: any, callback: Function) => void;
}

interface ReservationViewState {
    open: boolean;
}

class ReservationViewPage extends React.Component<ReservationViewProps, ReservationViewState> {
    fixMarkers: boolean = false;
    static defaultProps = {
        center: MapUtils.defaultCenter,
        zoom: MapUtils.defaultZoom,
    };

    state = {
        open: false,
    };

    componentDidMount() {
        this.load();
    }

    load = (): void => {
        // A failure recorded for the entity we come from would otherwise greet the next
        // one with the retry panel instead of the loader.
        this.props.clearError(reservationTypes.GET_RESERVATION);

        const { match } = this.props;
        const id = match.params.id;

        this.props.getReservation(id);
    };

    getBounds = (payload, maps) => {
        const bounds = new maps.LatLngBounds();
        if (payload.planned_start_location && MapUtils.checkLocation(payload.planned_start_location)) {
            bounds.extend(new maps.LatLng(payload.planned_start_location.lat, payload.planned_start_location.lng));
        }
        if (payload.planned_end_location && MapUtils.checkLocation(payload.planned_end_location)) {
            bounds.extend(new maps.LatLng(payload.planned_end_location.lat, payload.planned_end_location.lng));
        }

        return bounds;
    };

    addMarker = ({ maps, position, title, label, map }) => {
        return new maps.Marker({
            position,
            title,
            label,
            map,
        });
    };

    handleApiLoaded = (map, maps) => {
        const { t, match, reservations } = this.props;
        const id = match.params.id;
        const payload = reservations[id];

        const bounds = this.getBounds(payload, maps);
        map.fitBounds(bounds);

        if (!this.fixMarkers) {
            this.fixMarkers = true;

            if (payload.planned_end_location) {
                this.addMarker({
                    maps,
                    position: payload.planned_end_location,
                    title: t('booking:graph.goal'),
                    label: 'G',
                    map: map,
                });
            }
            if (payload.planned_start_location) {
                this.addMarker({
                    maps,
                    position: payload.planned_start_location,
                    title:
                        t('booking:graph.pick') +
                        (payload.planned_start_date
                            ? ' ' + FormattingUtils.format(payload.planned_start_date, FormatType.DateLong, t)
                            : ''),
                    label: 'P',
                    map: map,
                });
            }
        }
    };

    handleModeOpen = () => {
        this.setState((prevState) => ({ open: !prevState.open }));
    };

    handleCancelReservation = () => {
        const { match, reservations } = this.props;
        const id = match.params.id;
        const payload: Reservation = reservations[id];

        // Call without charge
        // this.props.cancelReservation(id, { description: 'Reservation View - Manual Cancel' }, () => {
        //     this.props.getReservation(id);
        // });

        // Call with potential charge
        this.props.cancelReservationCallCenter(
            { uuid: payload.uuid, phonenumber: payload.booker?.phone_number },
            () => {
                this.props.getReservation(id);
            },
        );
    };

    handleSplitReservation = () => {
        const { match, reservations } = this.props;
        const id = match.params.id;

        this.props.splitReservation(id, () => {
            this.props.getReservation(id);
            this.props.history.push(paths.reservations);
        });
    };

    getActiveBooking = (payload: Reservation): any => {
        let active_booking = payload.active_booking;
        if (!payload.active_booking) {
            let maxid = 0;
            let maxIndex = -1;

            payload.bookings.forEach((obj, index) => {
                if (obj.id > maxid) {
                    maxid = obj.id;
                    maxIndex = index;
                }
            });
            active_booking = payload.bookings[maxIndex];
        }
        return active_booking;
    };

    listItemBuilder = (payload: Reservation): ListCell[] => {
        const { t } = this.props;
        const { open } = this.state;
        if (!payload) {
            return [];
        }

        const active_booking = this.getActiveBooking(payload);

        const listItems: ListCell[] = [
            // { title: t('common:form.id'), data: payload.id },
            { title: t('common:form.number'), data: payload.number },
            {
                title: t('rider'),
                data: (
                    <MyLink color="secondary" to={`${paths.riders}/${payload.rider.id}`}>
                        {payload.rider.fullname}
                    </MyLink>
                ),
            },
            {
                title: t('booker'),
                data: payload.booker ? (
                    <MyLink color="secondary" to={`${paths.riders}/${payload.booker.id}`}>
                        {payload.booker.fullname}
                    </MyLink>
                ) : (
                    <MyLink color="secondary" to={`${paths.riders}/${payload.rider.id}`}>
                        {payload.rider.fullname}
                    </MyLink>
                ),
            },
            {
                title: t('company'),
                data: payload.company && (
                    <MyLink color="secondary" to={`${paths.companies}/${payload.company.id}`}>
                        {payload.company.name}
                    </MyLink>
                ),
                optional: true,
            },
            { title: t('external_reference'), data: payload.reference, optional: true },
            {
                title: t('cost_center'),
                data: getCostCenterDetails(payload.cost_center),
            },
            {
                title: t('ride.title'),
                data: payload.provision?.duration
                    ? `${t('ride.mad')} ${t('common:hour_from')} ${
                          parseFloat((payload.provision.duration / 60).toFixed(2)) + ' H'
                      }`
                    : payload.planned_end_location
                      ? t('ride.normal')
                      : t('ride.free'),
            },
            { title: t('ride_reason'), data: payload.reason },
            { title: t('dispatch_group'), data: payload.dispatch_group.name },
            { title: t('source'), data: payload.source },
            {
                title: t('booking'),
                data: active_booking && (
                    <>
                        <MyLink color="secondary" to={`${paths.bookings}/${active_booking.id}`}>
                            {active_booking.id}
                        </MyLink>
                        {payload.bookings.length > 1 && (
                            <>
                                <TooltipButton
                                    title={t(open ? 'see_less' : 'see_more')}
                                    icon={open ? <LessIcon fontSize="inherit" /> : <MoreIcon fontSize="inherit" />}
                                    onClick={this.handleModeOpen}
                                />
                                <Collapse in={open}>
                                    {payload.bookings
                                        .filter((b) => b.id !== active_booking.id)
                                        .map((b) => {
                                            return (
                                                <div key={b.id}>
                                                    <MyLink color="secondary" to={`${paths.bookings}/${b.id}`}>
                                                        {b.id}
                                                    </MyLink>
                                                </div>
                                            );
                                        })}
                                </Collapse>
                            </>
                        )}
                    </>
                ),
                optional: true,
            },
            {
                title: t('order'),
                data:
                    payload.order &&
                    `${FormattingUtils.price(payload.order.base_price_without_vat)} HT (${FormattingUtils.price(
                        payload.order.base_price_with_vat,
                    )} TTC)`,
                optional: true,
            },
            {
                title: t('onboard_payment'),
                data: payload.onboard_payment,
                type: FormatType.Boolean,
            },
            {
                title: t('nameboard_option'),
                data: payload.nameboard_option,
                type: FormatType.Boolean,
            },
            {
                title: t('reduction'),
                data: payload.promotion_usage
                    ? t('promo_code', { code: payload.promotion_usage?.promotion?.name })
                    : null,
                optional: true,
            },
            {
                title: t('favored_driver'),
                data: payload.favored_driver && (
                    <Link color="secondary" href={`${paths.drivers}/${payload.favored_driver.id}`}>
                        {`${payload.favored_driver.fullname} (${payload.favored_driver.id})`}
                    </Link>
                ),
                optional: true,
            },
            {
                title: t('driver_additional_info'),
                data: payload.driver_additional_info,
                optional: true,
            },
            {
                title: t('creation_date'),
                data: payload.creation_date,
                type: FormatType.DateLong,
            },
            {
                title: t('planned_start_date'),
                data: payload.planned_start_date,
                type: FormatType.DateLong,
            },
            {
                title: t('cancellation_date'),
                data: payload.cancellation_date,
                type: FormatType.DateLong,
                optional: true,
            },
            {
                title: t('cancellation_reason'),
                data: payload.cancellation_reason && `${t(payload.cancellation_reason)}`,
                optional: true,
            },
            {
                title: t('planned_start_location'),
                data:
                    payload.planned_start_location &&
                    `${AddressUtils.objectToAddress(
                        payload.planned_start_location.address,
                    )}${AddressUtils.tripConnectionToString(payload.trip_connection, !!payload.meeting_point)}`,
            },
            {
                title: t('planned_start_location_comments'),
                data: payload.planned_start_location?.comments,
                optional: true,
            },
            {
                title: t('planned_end_location'),
                data:
                    payload.planned_end_location && AddressUtils.objectToAddress(payload.planned_end_location.address),
            },
            {
                title: t('planned_end_location_comments'),
                data: payload.planned_end_location?.comments,
                optional: true,
            },
            {
                title: t('meeting_point'),
                data:
                    payload.meeting_point &&
                    AddressUtils.meetingPointToString(payload.meeting_point, payload.nameboard_option),
            },
        ];
        return listItems;
    };

    getMapLocation = (payload: Reservation): { center: Coords; zoom: number } => {
        const { center, zoom } = this.props;
        if (!payload) {
            return null;
        }

        const { planned_start_location, planned_end_location } = payload;

        return {
            center:
                planned_start_location && planned_end_location
                    ? {
                          lat: (planned_start_location.lat + planned_end_location.lat) / 2,
                          lng: (planned_start_location.lng + planned_end_location.lng) / 2,
                      }
                    : center,
            zoom: zoom,
        };
    };

    renderEditButton = (id: string) => {
        const { t, reservations } = this.props;
        const payload = reservations[id];
        return (
            UserUtils.checkRight(AccountRight.RIDE_DETAILS, RightRank.W) &&
            payload.status === ReservationStatus.CREATED && (
                <Button
                    color="secondary"
                    variant="contained"
                    component={RouterLink}
                    to={createPath(paths.reservationsEdit, { id })}
                >
                    {t('common:edit')}
                </Button>
            )
        );
    };

    renderOperationItemView = (payload: Reservation): JSX.Element[] => {
        const { globalClasses } = this.props;
        const operatorLogs = [
            ...payload.operator_logs,
            ...(payload.bookings ? payload.bookings.map((b) => b.operator_logs).flat() : []),
        ];
        return operatorLogs.map((item, key) => (
            <ListItem key={key}>
                <ListItemText>{item.sender.fullname + ' - ' + DateUtils.tsToDate(item.date)}</ListItemText>
                <ListItemText className={globalClasses.infoLabel}>{item.text}</ListItemText>
            </ListItem>
        ));
    };

    render() {
        const {
            t,
            globalClasses,
            match,
            reservations,
            getReservationError,
            splitReservationError,
            cancelReservationError,
            cancelReservationCallCenterError,
        } = this.props;
        const id = match.params.id;
        const payload = reservations[id];
        const map_location = this.getMapLocation(payload);

        return (
            <App
                needRights={AccountRight.RIDE_DETAILS}
                loading={!payload}
                loadingError={this.props.getReservationError}
                onRetry={this.load}
                header={{
                    icon: 'default',
                    title: t('title'),
                    subtitle: payload && `ID: ${payload.id}`,
                    bubble: {
                        visible: true,
                        text: payload?.status ? t('status.' + payload.status) : null,
                        colorEnable: payload?.status && !payload.status.includes('cancel'),
                    },
                    anchors: [
                        { href: '#general_infos', label: t('common:form.general_infos') },
                        { href: '#history', label: t('common:history') },
                    ],
                    button: payload &&
                        UserUtils.checkRights(AccountRight.RIDE_ACTION_CANCEL) &&
                        (payload.status === ReservationStatus.CREATED ||
                            payload.status === ReservationStatus.ALLOCATED) && (
                            <>
                                {payload.dispatch_group.id === 'van' && (
                                    <Dialog
                                        btnTitle={t('edit.is_split_van')}
                                        description={t('edit.hint_is_split_van')}
                                        customButton={
                                            <TooltipButton title={t('edit.is_split_van')}>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    color="primary"
                                                    style={{ padding: '6px 16px', marginRight: 32 }}
                                                    className={clsx(
                                                        globalClasses.btnAutoWidth,
                                                        globalClasses.btnWarning,
                                                    )}
                                                >
                                                    {t('split')}
                                                </Button>
                                            </TooltipButton>
                                        }
                                        onOK={this.handleSplitReservation}
                                    />
                                )}
                                <Dialog
                                    btnTitle={t('common:cancel')}
                                    dialogTitleColor={DialogTextColor.RED}
                                    description={t('common:cancelParam', { id })}
                                    customButton={
                                        <TooltipButton title={t('common:cancel')}>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                style={{ padding: '6px 16px' }}
                                                startIcon={<Icon fontSize="small">cancel</Icon>}
                                                className={clsx(globalClasses.btnAutoWidth, globalClasses.btnDanger)}
                                            >
                                                {t('common:cancel')}
                                            </Button>
                                        </TooltipButton>
                                    }
                                    btnOKText={t('common:cancel')}
                                    btnOKColor={DialogTextColor.RED}
                                    onOK={this.handleCancelReservation}
                                />
                            </>
                        ),
                }}
            >
                <ErrorMessage
                    payload={
                        getReservationError ||
                        cancelReservationError ||
                        cancelReservationCallCenterError ||
                        splitReservationError
                    }
                />
                {payload && (
                    <>
                        <Anchor id="general_infos" />
                        <EnhancedListItem
                            lock
                            datas={this.listItemBuilder(payload)}
                            title={t('common:form.general_infos')}
                            cardActions={this.renderEditButton(id)}
                        />
                        <br />
                        <AnchorBlock
                            id="operator_logs"
                            title={t('operator_logs')}
                            items={this.renderOperationItemView(payload)}
                        />
                        <br />
                        <div style={{ height: '50vh', width: '100%' }}>
                            <GoogleMapReact
                                bootstrapURLKeys={{ key: envConfig.google_key }}
                                yesIWantToUseGoogleMapApiInternals
                                onGoogleApiLoaded={({ map, maps }) => this.handleApiLoaded(map, maps)}
                                defaultCenter={map_location.center}
                                defaultZoom={map_location.zoom}
                            />
                        </div>
                        <br />
                        <ActionLogs history={payload.action_logs} withValues />
                    </>
                )}
            </App>
        );
    }
}

const ReservationViewPageWithTranslation = withGlobalStyles(
    withTranslation(['reservation', 'common'])(ReservationViewPage),
);

const mapStateToProps = (state) => ({
    reservations: reservationSelectors.reservations(state),
    getReservationError: errorSelectors.getReservationError(state),
    splitReservationError: errorSelectors.splitReservationError(state),
    cancelReservationError: errorSelectors.cancelReservationError(state),
    cancelReservationCallCenterError: errorSelectors.cancelReservationCallCenterError(state),
});

const mapDispatchToProps = {
    clearError: errorActions.clearError,
    getReservation: reservationActions.getReservation,
    splitReservation: reservationActions.splitReservation,
    cancelReservation: reservationActions.cancelReservation,
    cancelReservationCallCenter: reservationActions.cancelReservationCallCenter,
};

export default connect(mapStateToProps, mapDispatchToProps)(ReservationViewPageWithTranslation);
